import React from "react";
import { useNavigate } from "react-router-dom";
import useIsAdmin from "../../admin/hooks/useIsAdmin";
import goto from "../../assets/navigate.svg";

// Product identifiers remembered across the jump to the admin dashboard so it can
// open the matching history tab. Kept as named constants (no magic strings).
export const ADMIN_CONTEXT = { PNC: "pnc", SHOP_MANUAL: "shopmanual" };

/**
 * Navigate to the admin dashboard, recording which product launched it (so the
 * dashboard lands on the right history tab). Mirrors the secure-navigation flags
 * the module headers already set for in-app navigation.
 */
export function navigateToAdmin(navigate, context) {
  sessionStorage.setItem("navigation_in_progress", "true");
  sessionStorage.setItem("navigation_timestamp", Date.now().toString());
  sessionStorage.setItem("legitimate_navigation", "true");
  sessionStorage.setItem("app_initiated_navigation", "true");
  sessionStorage.setItem("navigation_target", "/admin");
  sessionStorage.setItem("admin_context", context);

  setTimeout(() => {
    navigate("/admin");
    setTimeout(() => {
      sessionStorage.removeItem("navigation_in_progress");
      sessionStorage.removeItem("app_initiated_navigation");
    }, 500);
  }, 50);
}

const ShieldIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
  </svg>
);

/**
 * Admin-dashboard entry button, shown only to admins. Shared by the PNC and
 * ShopManual headers so the markup + navigation live in one place.
 *
 * @param {Object}   props
 * @param {string}   props.context      which product opened it (ADMIN_CONTEXT.*)
 * @param {"desktop"|"mobile"} [props.variant]  header pill vs. mobile-menu row
 * @param {string}   [props.label]      button label (default "Admin")
 * @param {Function} [props.onNavigate] extra handler (e.g. close the mobile menu)
 */
export default function AdminButton({
  context,
  variant = "desktop",
  label = "Admin",
  onNavigate,
}) {
  const navigate = useNavigate();
  // Admin status is per-product — gate this button on the launching product's DB.
  const { isAdmin } = useIsAdmin(context);

  if (!isAdmin) return null;

  const handleClick = () => {
    if (onNavigate) onNavigate();
    navigateToAdmin(navigate, context);
  };

  if (variant === "mobile") {
    return (
      <div
        className="p-3 cursor-pointer hover:bg-red-50 transition-colors duration-150 flex items-center justify-between border-b border-gray-200"
        onClick={handleClick}
      >
        <div className="flex items-center">
          <ShieldIcon className="h-5 w-5 mr-2" />
          <span>{label}</span>
        </div>
        <img src={goto} alt="navigate" className="h-4 w-4" />
      </div>
    );
  }

  return (
    <div className="hidden sm:flex">
      <button
        className="flex items-center px-3 py-2 rounded-lg border border-gray-200 hover:bg-red-50 transition-colors duration-150 cursor-pointer bg-white whitespace-nowrap"
        onClick={handleClick}
        title="Admin Dashboard"
      >
        <ShieldIcon className="h-5 w-5 mr-2 flex-shrink-0" />
        <span className="text-sm routingFont">{label}</span>
      </button>
    </div>
  );
}
